import { create, fromJson, toJson, type JsonValue } from '@bufbuild/protobuf';
import { apiClient } from './request';
import {
  CreateInfiniMemoryRequestSchema,
  CreateInfiniMemoryResponseSchema,
  DeleteInfiniMemoryRequestSchema,
  DeleteInfiniMemoryResponseSchema,
  GetInfiniMemoryRequestSchema,
  GetInfiniMemoryResponseSchema,
  GetInfiniMemoryScopeRequestSchema,
  GetInfiniMemoryScopeResponseSchema,
  ListInfiniMemoriesRequestSchema,
  ListInfiniMemoriesResponseSchema,
  ListInfiniMemoryStoresRequestSchema,
  UpdateInfiniMemoryRequestSchema,
  UpdateInfiniMemoryResponseSchema,
  type CreateInfiniMemoryData,
  type DeleteInfiniMemoryData,
  type GetInfiniMemoryScopeData,
  type InfiniMemoryDetail,
  type ListInfiniMemoriesData,
  type UpdateInfiniMemoryData,
} from '@/proto/agentplatform/httpv2/memory_pb';

async function post(path: string, body: JsonValue): Promise<JsonValue> {
  const response = await apiClient.post<JsonValue>(path, body);
  return response.data;
}

export interface InfiniMemoryScopeInput {
  prefix: string;
  storeId: string;
}

// The current infini-memory integration intentionally uses one shared user namespace.
// Keep this fixed until the storage contract is explicitly migrated to per-user scopes.
const INFINI_MEMORY_USER_ID = 'infiniai';

export interface InfiniMemoryStore {
  id: string;
  name: string;
  description: string;
  tenantId: string;
  userId: string;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number;
}

export interface InfiniMemoryStoreList {
  stores: string[];
  items: InfiniMemoryStore[];
  total: number;
}

interface StoreEnvelope<T> {
  data?: T;
}

function storeEnvelopeData<T>(raw: JsonValue): T {
  const data = (raw as unknown as StoreEnvelope<T>).data;
  if (!data) throw new Error('empty infini-memory store response');
  return data;
}

export const infiniMemoryApi = {
  async getScope(): Promise<GetInfiniMemoryScopeData> {
    const req = create(GetInfiniMemoryScopeRequestSchema, {});
    const response = fromJson(
      GetInfiniMemoryScopeResponseSchema,
      await post('/v2/GetInfiniMemoryScope', toJson(GetInfiniMemoryScopeRequestSchema, req))
    );
    if (!response.data) throw new Error('empty infini-memory scope response');
    return response.data;
  },

  async listStores(prefix: string, query = '', limit = 200): Promise<string[]> {
    const result = await infiniMemoryApi.listStoreConfigs(prefix, query, limit);
    return result.stores;
  },

  async listStoreConfigs(prefix: string, query = '', limit = 200): Promise<InfiniMemoryStoreList> {
    const req = create(ListInfiniMemoryStoresRequestSchema, { prefix, query, limit });
    const raw = await post(
      '/v2/ListInfiniMemoryStores',
      toJson(ListInfiniMemoryStoresRequestSchema, req)
    );
    const data = storeEnvelopeData<Partial<InfiniMemoryStoreList>>(raw);
    return { stores: data.stores ?? [], items: data.items ?? [], total: data.total ?? 0 };
  },

  async createStore(input: { name: string; description: string }): Promise<InfiniMemoryStore> {
    const raw = await post('/v2/CreateInfiniMemoryStore', { store: input });
    return storeEnvelopeData<InfiniMemoryStore>(raw);
  },

  async updateStore(
    id: string,
    input: { name: string; description: string }
  ): Promise<InfiniMemoryStore> {
    const raw = await post('/v2/UpdateInfiniMemoryStore', { id, store: input });
    return storeEnvelopeData<InfiniMemoryStore>(raw);
  },

  async deleteStore(id: string): Promise<void> {
    await post('/v2/DeleteInfiniMemoryStore', { id });
  },

  async listMemories(
    scope: InfiniMemoryScopeInput,
    options: {
      query?: string;
      kind?: 'all' | 'current' | 'memory';
      offset?: number;
      limit?: number;
    }
  ): Promise<ListInfiniMemoriesData> {
    const req = create(ListInfiniMemoriesRequestSchema, {
      ...scope,
      userId: INFINI_MEMORY_USER_ID,
      query: options.query ?? '',
      kind: options.kind ?? 'all',
      offset: options.offset ?? 0,
      limit: options.limit ?? 30,
    });
    const response = fromJson(
      ListInfiniMemoriesResponseSchema,
      await post('/v2/ListInfiniMemories', toJson(ListInfiniMemoriesRequestSchema, req))
    );
    if (!response.data) throw new Error('empty infini-memory list response');
    return response.data;
  },

  async getMemory(documentId: string, scope: InfiniMemoryScopeInput): Promise<InfiniMemoryDetail> {
    const req = create(GetInfiniMemoryRequestSchema, {
      documentId,
      ...scope,
      userId: INFINI_MEMORY_USER_ID,
    });
    const response = fromJson(
      GetInfiniMemoryResponseSchema,
      await post('/v2/GetInfiniMemory', toJson(GetInfiniMemoryRequestSchema, req))
    );
    if (!response.data?.document) throw new Error('empty infini-memory detail response');
    return response.data.document;
  },

  async createMemory(
    scope: InfiniMemoryScopeInput,
    content: string
  ): Promise<CreateInfiniMemoryData> {
    const req = create(CreateInfiniMemoryRequestSchema, {
      ...scope,
      userId: INFINI_MEMORY_USER_ID,
      content,
    });
    const response = fromJson(
      CreateInfiniMemoryResponseSchema,
      await post('/v2/CreateInfiniMemory', toJson(CreateInfiniMemoryRequestSchema, req))
    );
    if (!response.data) throw new Error('empty infini-memory create response');
    return response.data;
  },

  async updateMemory(
    documentId: string,
    scope: InfiniMemoryScopeInput,
    content: string,
    revision: string
  ): Promise<UpdateInfiniMemoryData> {
    const req = create(UpdateInfiniMemoryRequestSchema, {
      documentId,
      ...scope,
      userId: INFINI_MEMORY_USER_ID,
      content,
      revision,
    });
    const response = fromJson(
      UpdateInfiniMemoryResponseSchema,
      await post('/v2/UpdateInfiniMemory', toJson(UpdateInfiniMemoryRequestSchema, req))
    );
    if (!response.data) throw new Error('empty infini-memory update response');
    return response.data;
  },

  async deleteMemory(
    documentId: string,
    scope: InfiniMemoryScopeInput
  ): Promise<DeleteInfiniMemoryData> {
    const req = create(DeleteInfiniMemoryRequestSchema, {
      documentId,
      ...scope,
      userId: INFINI_MEMORY_USER_ID,
    });
    const response = fromJson(
      DeleteInfiniMemoryResponseSchema,
      await post('/v2/DeleteInfiniMemory', toJson(DeleteInfiniMemoryRequestSchema, req))
    );
    if (!response.data) throw new Error('empty infini-memory delete response');
    return response.data;
  },
};
